import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLocalization } from '../contexts/LocalizationContext';
import axios from 'axios';
import ShortcodeParser from '../components/shortcodes/ShortcodeParser';
import NotFoundPage from './NotFoundPage';
import AboutPage from './AboutPage';
import SEOHead from '../components/SEOHead';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Slugs that render with the bespoke editorial layout instead of the
// default CMS markup. Keep this list small and intentional.
const EDITORIAL_SLUGS = new Set(['about-us', 'quienes-somos', 'quienes_somos']);

const PageView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { countryCode } = useLocalization();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isEditorial = EDITORIAL_SLUGS.has(String(slug || '').toLowerCase());

  useEffect(() => {
    if (countryCode && slug && !isEditorial) {
      fetchPage();
    } else if (isEditorial) {
      setLoading(false);
    }
  }, [slug, countryCode, isEditorial]);

  const fetchPage = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/pages/${slug}`, {
        params: { 
          country: countryCode,
          _t: Date.now() // Cache busting
        }
      });
      setPage(response.data);
    } catch (error) {
      console.error('Error fetching page:', error);
      setError('Page not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (isEditorial) {
    return <AboutPage />;
  }

  if (error || !page) {
    return <NotFoundPage />;
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <SEOHead
        title={page.metaTitle || page.title}
        description={page.metaDescription}
        url={`/page/${slug}`}
      />
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">{page.title}</h1>
        
        {/* Use ShortcodeParser instead of dangerouslySetInnerHTML */}
        <div className="page-content prose prose-lg max-w-none">
          <ShortcodeParser content={page.content} />
        </div>
        
        <style>{`
          .page-content h1 {
            font-size: 2rem;
            font-weight: bold;
            margin-top: 2rem;
            margin-bottom: 1rem;
            color: #1a202c;
          }
          .page-content h2 {
            font-size: 1.5rem;
            font-weight: bold;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
            color: #2d3748;
          }
          .page-content h3 {
            font-size: 1.25rem;
            font-weight: 600;
            margin-top: 1rem;
            margin-bottom: 0.5rem;
            color: #2d3748;
          }
          .page-content p {
            margin-bottom: 1rem;
            line-height: 1.75;
            color: #4a5568;
          }
          .page-content ul, .page-content ol {
            margin-bottom: 1rem;
            padding-left: 1.5rem;
          }
          .page-content li {
            margin-bottom: 0.5rem;
            color: #4a5568;
          }
          .page-content ul {
            list-style-type: disc;
          }
          .page-content ol {
            list-style-type: decimal;
          }
          .page-content strong {
            font-weight: 600;
            color: #1a202c;
          }
          .page-content a {
            color: #be1f1a;
            text-decoration: underline;
          }
          .page-content a:hover {
            color: #a71b17;
          }
          .page-content table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 1.5rem;
          }
          .page-content th, .page-content td {
            border: 1px solid #e2e8f0;
            padding: 0.75rem;
            text-align: left;
          }
          .page-content th {
            background-color: #f7fafc;
            font-weight: 600;
          }
          .page-content blockquote {
            border-left: 4px solid #be1f1a;
            padding-left: 1rem;
            margin: 1rem 0;
            color: #4a5568;
            font-style: italic;
          }
          .page-content code {
            background-color: #f7fafc;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            font-family: monospace;
            font-size: 0.875rem;
          }
          .page-content pre {
            background-color: #2d3748;
            color: #f7fafc;
            padding: 1rem;
            border-radius: 0.5rem;
            overflow-x: auto;
            margin-bottom: 1rem;
          }
          .page-content pre code {
            background-color: transparent;
            padding: 0;
          }
        `}</style>
      </div>
    </div>
  );
};

export default PageView;
